import React, { useEffect, useRef, useState } from "react";
import api, { formatDateTime, formatErrorDetail } from "@/lib/api";
import { PageHeader, Surface, Empty } from "@/components/app/Primitives";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Plus, Fingerprint, Upload, KeyRound, Copy, Trash2, RefreshCw, AlertTriangle, Radio } from "lucide-react";

/* Biometric terminals — register a device, hand it a key, then let it push
 * punches. HR can also upload the file the terminal exported. */

export default function BiometricDevices() {
    const [devices, setDevices] = useState([]);
    const [imports, setImports] = useState([]);
    const [unmatched, setUnmatched] = useState([]);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ name: "", serial: "", location: "", match_by: "biometric_id" });
    const [newKey, setNewKey] = useState(null);      // {name, api_key}
    const [uploadFor, setUploadFor] = useState("");  // device id chosen for the file upload
    const [busy, setBusy] = useState(false);
    const fileRef = useRef(null);

    const load = () => Promise.all([
        api.get("/biometric/devices").then((r) => setDevices(r.data.items || [])),
        api.get("/biometric/imports").then((r) => setImports(r.data.items || [])),
        api.get("/biometric/unmatched").then((r) => setUnmatched(r.data.items || [])),
    ]).catch(() => {});
    useEffect(() => { load(); }, []);

    const create = async () => {
        if (!form.name.trim()) return toast.error("Give the terminal a name");
        try {
            const { data } = await api.post("/biometric/devices", form);
            setNewKey({ name: data.name, api_key: data.api_key });
            setOpen(false); setForm({ name: "", serial: "", location: "", match_by: "biometric_id" });
            load();
        } catch (e) { toast.error(formatErrorDetail(e.response?.data?.detail) || "Could not add the device"); }
    };

    const rotate = async (d) => {
        if (!window.confirm(`Issue a new key for ${d.name}? The terminal will stop sending until you update it.`)) return;
        try {
            const { data } = await api.post(`/biometric/devices/${d.id}/rotate-key`);
            setNewKey({ name: d.name, api_key: data.api_key });
            load();
        } catch { toast.error("Could not rotate the key"); }
    };

    const remove = async (d) => {
        if (!window.confirm(`Remove ${d.name}? Punches already imported are kept.`)) return;
        try { await api.delete(`/biometric/devices/${d.id}`); toast.success("Device removed"); load(); }
        catch { toast.error("Could not remove the device"); }
    };

    const toggleActive = async (d) => {
        try { await api.patch(`/biometric/devices/${d.id}`, { active: !d.active }); load(); }
        catch { toast.error("Could not update the device"); }
    };

    const upload = async (file) => {
        if (!file) return;
        setBusy(true);
        const fd = new FormData();
        fd.append("file", file);
        try {
            const { data } = await api.post(`/biometric/upload${uploadFor ? `?device_id=${uploadFor}` : ""}`, fd,
                { headers: { "Content-Type": "multipart/form-data" } });
            toast.success(`${data.accepted} punches imported → ${data.attendance_rows} attendance day${data.attendance_rows === 1 ? "" : "s"} updated`);
            if (data.unmatched) toast.warning(`${data.unmatched} punches from badges not enrolled to an employee`);
            if (data.locked_skipped) toast.warning(`${data.locked_skipped} day(s) skipped — already locked by payroll`);
            load();
        } catch (e) {
            toast.error(formatErrorDetail(e.response?.data?.detail) || "Could not read that file");
        } finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
    };

    const copy = (t) => navigator.clipboard?.writeText(t).then(() => toast.success("Key copied"), () => {});

    return (
        <div>
            <PageHeader
                title="Biometric Terminals"
                subtitle="Fingerprint and face terminals push punches straight into attendance. Hours and lateness are computed against each employee's shift; locked payroll days are never touched."
                actions={
                    <div className="flex items-center gap-2">
                        <select className="h-9 rounded-md border border-border bg-background px-2 text-sm"
                                value={uploadFor} onChange={(e) => setUploadFor(e.target.value)} data-testid="bio-upload-device">
                            <option value="">Upload as… (no device)</option>
                            {devices.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                        </select>
                        <button className="h-9 px-3 rounded-md border border-border text-sm flex items-center gap-2"
                                disabled={busy} onClick={() => fileRef.current?.click()} data-testid="bio-upload">
                            <Upload className="size-4" /> {busy ? "Importing…" : "Upload punch file"}
                        </button>
                        <input ref={fileRef} type="file" accept=".csv,.txt,.dat,.tsv" className="hidden"
                               onChange={(e) => upload(e.target.files?.[0])} data-testid="bio-file" />
                        <button className="h-9 px-3 rounded-md bg-primary text-white text-sm font-medium flex items-center gap-2"
                                onClick={() => setOpen(true)} data-testid="bio-add">
                            <Plus className="size-4" /> Add terminal
                        </button>
                    </div>
                } />

            {!devices.length && (
                <Surface className="p-8 text-center" data-testid="bio-empty">
                    <Fingerprint className="size-10 mx-auto text-muted-foreground/40 mb-3" />
                    <h3 className="font-semibold">No terminals registered</h3>
                    <p className="text-sm text-muted-foreground mt-1 max-w-lg mx-auto">
                        Add a terminal to get its API key, then point the device at
                        <code className="mx-1 px-1.5 py-0.5 rounded bg-muted text-xs">POST /api/biometric/punches</code>
                        with an <code className="px-1.5 py-0.5 rounded bg-muted text-xs">X-Device-Key</code> header.
                        No terminal? Export its log and use “Upload punch file”.
                    </p>
                </Surface>
            )}

            {devices.length > 0 && (
                <div className="grid gap-3 md:grid-cols-2">
                    {devices.map((d) => (
                        <Surface key={d.id} className="p-4" data-testid={`bio-device-${d.id}`}>
                            <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                    <div className="flex items-center gap-2">
                                        <Fingerprint className="size-4 text-primary shrink-0" />
                                        <span className="font-semibold truncate">{d.name}</span>
                                        <span className={`text-[10px] px-2 py-0.5 rounded-full border ${d.active
                                            ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                                            : "bg-muted text-muted-foreground border-border"}`}>
                                            {d.active ? "Active" : "Paused"}
                                        </span>
                                    </div>
                                    <div className="text-xs text-muted-foreground mt-1 space-y-0.5">
                                        {d.serial && <div>Serial {d.serial}</div>}
                                        {d.location && <div>{d.location}</div>}
                                        <div>Matches employees by <b>{d.match_by === "employee_no" ? "employee no." : "biometric ID"}</b></div>
                                        <div className="font-mono">key …{d.key_hint} · {d.total_punches || 0} punches</div>
                                        <div className={d.last_seen_at ? "" : "text-amber-700"}>
                                            {d.last_seen_at ? <><Radio className="size-3 inline mr-1" />Last seen {formatDateTime(d.last_seen_at)}</>
                                                            : "Never connected"}
                                        </div>
                                    </div>
                                </div>
                                <div className="flex flex-col gap-1 shrink-0">
                                    <button className="text-xs px-2 py-1 rounded border border-border" onClick={() => toggleActive(d)}>
                                        {d.active ? "Pause" : "Resume"}
                                    </button>
                                    <button className="text-xs px-2 py-1 rounded border border-border flex items-center gap-1" onClick={() => rotate(d)}>
                                        <RefreshCw className="size-3" /> New key
                                    </button>
                                    <button className="text-xs px-2 py-1 rounded border border-destructive/40 text-destructive flex items-center gap-1" onClick={() => remove(d)}>
                                        <Trash2 className="size-3" /> Remove
                                    </button>
                                </div>
                            </div>
                        </Surface>
                    ))}
                </div>
            )}

            {unmatched.length > 0 && (
                <Surface className="p-4 mt-4 border-amber-300 bg-amber-50/40" data-testid="bio-unmatched">
                    <h3 className="text-sm font-semibold flex items-center gap-1.5 text-amber-900">
                        <AlertTriangle className="size-4" /> Badges not linked to an employee
                    </h3>
                    <p className="text-xs text-amber-800 mt-1">
                        These IDs punched in but don't match anyone. Set the Biometric ID on the employee's profile so their hours are recorded.
                    </p>
                    <div className="flex flex-wrap gap-1.5 mt-2">
                        {unmatched.map((u) => (
                            <span key={u.identifier} className="text-xs font-mono px-2 py-1 rounded bg-white border border-amber-200">
                                {u.identifier} <span className="text-amber-700">×{u.count}</span>
                            </span>
                        ))}
                    </div>
                </Surface>
            )}

            {imports.length > 0 && (
                <Surface className="p-0 mt-4 overflow-hidden">
                    <h3 className="text-sm font-semibold px-4 pt-4 pb-2">Recent imports</h3>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-muted/40">
                                <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                                    <th className="px-3 py-2">When</th><th className="px-3 py-2">Source</th>
                                    <th className="px-3 py-2 text-right">Received</th><th className="px-3 py-2 text-right">New</th>
                                    <th className="px-3 py-2 text-right">Repeats</th><th className="px-3 py-2 text-right">Unmatched</th>
                                    <th className="px-3 py-2 text-right">Days updated</th><th className="px-3 py-2 text-right">Locked</th>
                                </tr>
                            </thead>
                            <tbody>
                                {imports.map((b) => (
                                    <tr key={b.id} className="border-t border-border">
                                        <td className="px-3 py-2 text-xs text-muted-foreground">{formatDateTime(b.imported_at)}</td>
                                        <td className="px-3 py-2">{b.device_name}</td>
                                        <td className="px-3 py-2 text-right font-mono">{b.received}</td>
                                        <td className="px-3 py-2 text-right font-mono text-emerald-700">{b.accepted}</td>
                                        <td className="px-3 py-2 text-right font-mono text-muted-foreground">{b.duplicates}</td>
                                        <td className="px-3 py-2 text-right font-mono text-amber-700">{b.unmatched || 0}</td>
                                        <td className="px-3 py-2 text-right font-mono">{b.attendance_rows}</td>
                                        <td className="px-3 py-2 text-right font-mono text-muted-foreground">{b.locked_skipped || 0}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </Surface>
            )}

            {/* Add terminal */}
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-lg">
                    <DialogHeader><DialogTitle className="flex items-center gap-2"><Fingerprint className="size-4 text-primary" /> Add a terminal</DialogTitle></DialogHeader>
                    <div className="space-y-2">
                        <div><Label>Name</Label><Input value={form.name} placeholder="Main Gate Terminal"
                                                       onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="bio-name" /></div>
                        <div className="grid grid-cols-2 gap-2">
                            <div><Label>Serial (optional)</Label><Input value={form.serial} onChange={(e) => setForm({ ...form, serial: e.target.value })} /></div>
                            <div><Label>Location (optional)</Label><Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} /></div>
                        </div>
                        <div>
                            <Label>Match punches to employees by</Label>
                            <select className="h-9 w-full rounded-md border border-border bg-background px-2 text-sm"
                                    value={form.match_by} onChange={(e) => setForm({ ...form, match_by: e.target.value })}>
                                <option value="biometric_id">Biometric ID (the enrolment number on the device)</option>
                                <option value="employee_no">Employee number</option>
                            </select>
                            <p className="text-xs text-muted-foreground mt-1">Both are checked either way — this only sets which one to try first.</p>
                        </div>
                    </div>
                    <DialogFooter><button className="h-9 px-3 rounded-md bg-primary text-white text-sm" onClick={create} data-testid="bio-save">Add terminal</button></DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Key shown once */}
            <Dialog open={!!newKey} onOpenChange={(v) => !v && setNewKey(null)}>
                <DialogContent className="max-w-lg">
                    <DialogHeader><DialogTitle className="flex items-center gap-2"><KeyRound className="size-4 text-primary" /> Key for {newKey?.name}</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                        <div className="rounded-md bg-amber-50 border border-amber-300 p-3 text-xs text-amber-900">
                            Copy this now — it is not shown again. If it's lost, issue a new key.
                        </div>
                        <div className="flex items-center gap-2">
                            <Input readOnly value={newKey?.api_key || ""} className="font-mono text-xs" onFocus={(e) => e.target.select()} data-testid="bio-key" />
                            <button className="h-9 px-3 rounded-md bg-secondary text-sm flex items-center gap-1 shrink-0" onClick={() => copy(newKey.api_key)}>
                                <Copy className="size-4" /> Copy
                            </button>
                        </div>
                        <div className="text-xs text-muted-foreground">
                            <div className="font-semibold mb-1">Point the terminal at:</div>
                            <pre className="bg-muted rounded-md p-2.5 overflow-x-auto text-[11px] leading-relaxed">{`POST ${window.location.origin}/api/biometric/punches
X-Device-Key: ${newKey?.api_key || ""}
Content-Type: application/json

{"punches": [
  {"identifier": "1042", "timestamp": "2026-07-18 08:03:11", "direction": "in"},
  {"identifier": "1042", "timestamp": "2026-07-18 17:32:40", "direction": "out"}
]}`}</pre>
                        </div>
                    </div>
                    <DialogFooter><button className="h-9 px-3 rounded-md bg-primary text-white text-sm" onClick={() => setNewKey(null)}>Done</button></DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
